/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { toJS } from 'mobx';
import * as constants from '../../../common/constants';

const typeString = types.model('typeString', {
  S: types.maybeNull(types.string)
});
const ExclusiveStartKeyModel = types.model('ExclusiveStartKeyModel', {
  bookId: types.maybeNull(typeString),
  serverDateTime: types.maybeNull(typeString),
  type: types.maybeNull(typeString),
  feedbackId: types.maybeNull(typeString),
  userId: types.maybeNull(typeString),
  channelsId: types.maybeNull(typeString),
  referenceId: types.maybeNull(typeString)
});

const ExclusiveStartKey = types.model('ExclusiveStartKey', {
  ExclusiveStartKey: types.optional(ExclusiveStartKeyModel, {})
});

const DataItem = types.model('DataItem', {
  bookId: types.maybeNull(types.string),
  comments: types.maybeNull(types.string),
  feedback: types.maybeNull(types.string),
  feedbackContent: types.maybeNull(types.array(types.union(types.string, types.frozen()), [])),
  reasonForDisLike: types.maybeNull(types.array(types.string, [])),
  requestId: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  displayType: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  channelsId: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  pageId: types.maybeNull(types.string),
  questionId: types.maybeNull(types.string),
  topic: types.maybeNull(types.string)
});

const AllBooksLastKeyItems = types.model('AllBooksLastKeyItems', {
  type: types.maybeNull(types.string),
  isNextPageKey: types.optional(ExclusiveStartKey, {}),
  isNextPageKeyAudit: types.optional(ExclusiveStartKey, {})
});

const FeedbackModel = types
  .model('FeedbackModel', {
    data: types.optional(types.array(DataItem), []),
    allBooksSearchFlag: types.optional(types.boolean, false),
    allBooksLastKey: types.optional(types.array(AllBooksLastKeyItems), []),
    isNextPageKey: types.optional(ExclusiveStartKey, {}),
    isNextPageKeyAudit: types.optional(ExclusiveStartKey, {}),
    pendingApiCalls: types.optional(types.number, 0),
    isCustomLoading: types.optional(types.boolean, false)
  })
  .views((self) => ({
    get feedbackData() {
      return toJS(self.data);
    }
  }))
  .actions((self) => ({
    setAllBooksSearchFlag(boolValue) {
      self.allBooksSearchFlag = boolValue;
    },
    appendToAllBooksLastKey(newItem) {
      self.allBooksLastKey.push(newItem);
    },
    fetch(payload) {
      // Increment pending API calls counter
      this.incrementPendingApiCalls();
      Framework.getEventManager().publish(constants.GET_FEEDBACK_DATA, {
        payload
      });
    },
    incrementPendingApiCalls() {
      self.pendingApiCalls += 1;
      self.isCustomLoading = true;
    },
    decrementPendingApiCalls() {
      if (self.pendingApiCalls > 0) {
        self.pendingApiCalls -= 1;
        if (self.pendingApiCalls === 0) {
          self.isCustomLoading = false;
        }
      }
    },
    resetApiCallTracking() {
      self.pendingApiCalls = 0;
      self.isCustomLoading = false;
    },
    resetPageNextKey() {
      self.isNextPageKey = undefined;
      self.isNextPageKeyAudit = undefined;
      this.resetApiCallTracking();
    },
    setResponse(response) {
      this.decrementPendingApiCalls();
      if (self.allBooksSearchFlag) {
        const { isNextPageKey, isNextPageKeyAudit } = response.data;
        const firstObject = response.data.data?.[0];
        if (!_.isEmpty(isNextPageKey) && !_.isEmpty(isNextPageKey.ExclusiveStartKey)) {
          self.appendToAllBooksLastKey({ type: firstObject?.type, isNextPageKey });
        }
        if (!_.isEmpty(isNextPageKeyAudit) && !_.isEmpty(isNextPageKeyAudit.ExclusiveStartKey)) {
          self.appendToAllBooksLastKey({ type: firstObject?.type, isNextPageKeyAudit });
        }
      } else {
        applySnapshot(self.isNextPageKey, response?.data?.isNextPageKey || {});
        applySnapshot(self.isNextPageKeyAudit, response?.data?.isNextPageKeyAudit || {});
      }
      const prevData = toJS(self.data);
      const dataToSet = [...prevData, ...(response?.data?.data || [])];
      applySnapshot(self.data, dataToSet);
      Framework.getEventManager().publish(constants.GET_FEEDBACK_RESPONSE);
    },
    resetStoreValues() {
      this.decrementPendingApiCalls();
      const initialState = [];
      applySnapshot(self.data, initialState);
      applySnapshot(self.isNextPageKey, {});
      applySnapshot(self.isNextPageKeyAudit, {});
      Framework.getEventManager().publish(constants.GET_FEEDBACK_RESPONSE);
    },

    resetAllBooksLastKey() {
      const initialState = [];
      applySnapshot(self.allBooksLastKey, initialState);
    }
  }));

export default FeedbackModel;
